import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ClipboardList,
  Clock,
  ExternalLink,
  ShieldAlert,
  Sparkles,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";
import AdminClientRequestFilters, { type ClientRequestFilterValues } from "./AdminClientRequestFilters";
import AdminClientRequestCard, { type AdminClientRequestCardRow } from "./AdminClientRequestCard";
import AdminClientRequestDetailDrawer from "./AdminClientRequestDetailDrawer";

type Summary = {
  pending?: number;
  urgent?: number;
  inProgress?: number;
  resolvedToday?: number;
};

export default function AdminClientRequestsPage(): React.JSX.Element {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rows, setRows] = useState<AdminClientRequestCardRow[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ClientRequestFilterValues>({ q: "", type: "", status: "" });
  const [page, setPage] = useState(1);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.get<{ pending?: number; urgent?: number; inProgress?: number; resolvedToday?: number }>(
        "/supervisor/client-requests/summary"
      );
      setSummary({
        pending: data.pending,
        urgent: data.urgent,
        inProgress: data.inProgress,
        resolvedToday: data.resolvedToday,
      });
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{
        data?: AdminClientRequestCardRow[];
        meta?: { total?: number; page?: number; totalPages?: number };
      }>("/supervisor/client-requests", {
        params: {
          page,
          limit: 24,
          q: filters.q.trim() || undefined,
          type: filters.type.trim() || undefined,
          status: filters.status.trim() || undefined,
        },
      });
      setRows(Array.isArray(data.data) ? data.data : []);
      setMeta({
        total: data.meta?.total ?? 0,
        page: data.meta?.page ?? page,
        totalPages: data.meta?.totalPages ?? 1,
      });
    } catch {
      toast.error("Impossible de charger les demandes.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters.q, filters.type, filters.status]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const kpis = useMemo(
    () => [
      { label: "En attente", value: summary?.pending ?? "—", icon: Clock },
      { label: "Urgentes", value: summary?.urgent ?? "—", icon: ShieldAlert },
      { label: "En cours / assignées", value: summary?.inProgress ?? "—", icon: Users },
      { label: "Résolues aujourd’hui", value: summary?.resolvedToday ?? "—", icon: Sparkles },
    ],
    [summary]
  );

  const clientOrigin = window.location.origin;
  const openPortal = () => {
    window.open(`${clientOrigin}/client/login`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-full space-y-6 text-slate-100">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-emerald-400/90">Administration</p>
          <h1 className="text-2xl font-semibold text-white">Demandes client</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Demandes portail (passage, urgence) et réclamations — traitement superviseur / planificateur,
            aligné sur l’espace portail client.
          </p>
        </div>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <article
            key={k.label}
            className="rounded-xl border border-white/10 bg-slate-900/80 p-4 shadow-lg shadow-black/30 backdrop-blur"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-400">{k.label}</p>
              <k.icon className="h-4 w-4 text-emerald-400/80" aria-hidden />
            </div>
            <p className="mt-3 text-2xl font-semibold tabular-nums text-white">
              {summaryLoading ? "…" : k.value}
            </p>
          </article>
        ))}
      </section>

      <section className="rounded-xl border border-white/10 bg-slate-900/60 p-4 shadow-inner shadow-black/40">
        <h2 className="text-sm font-semibold text-white">Actions rapides</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/admin/portail-client"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <Users className="h-4 w-4 text-emerald-400" />
            Portail client (accès)
            <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
          </Link>
          <Link
            to="/admin/planificateur"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <Clock className="h-4 w-4 text-emerald-400" />
            Planificateur — modifications
          </Link>
          <Link
            to="/admin/interventions"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <ShieldAlert className="h-4 w-4 text-emerald-400" />
            Interventions / urgences
          </Link>
          <button
            type="button"
            onClick={openPortal}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-600/20 px-3 py-2 text-sm font-medium text-emerald-100 transition hover:bg-emerald-600/30"
          >
            <ExternalLink className="h-4 w-4" />
            Ouvrir lien portail client
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/80 shadow-xl shadow-black/40">
        <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-emerald-400" aria-hidden />
            <h2 className="text-sm font-semibold text-white">Liste des demandes</h2>
          </div>
          <button
            type="button"
            onClick={() => {
              void loadSummary();
              void loadList();
            }}
            className="self-start text-xs font-medium text-emerald-400 hover:text-emerald-300 sm:self-auto"
          >
            Rafraîchir
          </button>
        </div>

        <div className="border-b border-white/10 bg-slate-950/40 p-4">
          <AdminClientRequestFilters value={filters} onChange={(v) => { setPage(1); setFilters(v); }} />
        </div>

        <div className="p-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Chargement…</p>
          ) : rows.length === 0 ? (
            <p className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 px-4 py-10 text-center text-sm text-slate-400">
              Aucune demande pour ces filtres.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rows.map((row) => (
                <AdminClientRequestCard
                  key={String(row.publicId ?? row.reference ?? row.id)}
                  row={row}
                  onOpen={(id) => setDrawerId(id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-slate-400">
          <span>
            Page {meta.page} / {meta.totalPages} · {meta.total} éléments
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-white/10 bg-slate-800/80 px-3 py-1.5 font-medium text-slate-200 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Précédent
            </button>
            <button
              type="button"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-white/10 bg-slate-800/80 px-3 py-1.5 font-medium text-slate-200 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        </div>
      </section>

      <AdminClientRequestDetailDrawer
        open={drawerId != null}
        publicId={drawerId}
        onClose={() => setDrawerId(null)}
        onChanged={() => {
          void loadSummary();
          void loadList();
        }}
      />
    </div>
  );
}
